import { useCallback, useEffect, useState } from 'react';

import { getClientTimeout } from '../../services/clientTimeoutConfig';
import { signalInsufficientCredits } from '../../services/creditGateEvents';
import { eventService } from '../../services/eventService';
import { projectService, ActionBarClick, type PlanningActionSelectionInput } from '../../services/projectService';
import { showPlanningTasks } from '../PlanningTasks';

interface Store {
  projectId: string | null;
  sessionId: string | null;
  clicks: ActionBarClick[];
  byKey: Map<string, ActionBarClick>;
  loaded: boolean;
}

const store: Store = {
  projectId: null,
  sessionId: null,
  clicks: [], byKey: new Map(),
  loaded: false,
};

type Listener = () => void;
const listeners = new Set<Listener>();

const keyOf = (entryId: string, buttonKey: string) => `${entryId}:${buttonKey}`;

function emit() { listeners.forEach((l) => l()); }

function updateClicks(next: ActionBarClick[]) {
  store.clicks = next;
  store.byKey = new Map(next.map((c) => [keyOf(c.entry_id, c.button_key), c] as const));
  emit();
}

export async function loadActionBarClicks(projectId: string, force = false): Promise<void> {
  if (!force && store.projectId === projectId && store.sessionId === null && store.loaded) return;
  store.projectId = projectId;
  store.sessionId = null;
  try {
    const clicks = await projectService.getActionBarClicks(projectId);

    updateClicks(clicks);
    store.loaded = true;
  } catch {
    // Keep any optimistic entries we already have.
  }
}

// eslint-disable-next-line max-statements
export async function loadSessionActionBarClicks(
  projectId: string,
  sessionId: string | null | undefined,
  force = false,
): Promise<void> {
  const normalizedSessionId = sessionId ?? null;

  if (
    !force
    && store.projectId === projectId
    && store.sessionId === normalizedSessionId
    && store.loaded
  ) return;
  store.projectId = projectId;
  store.sessionId = normalizedSessionId;
  try {
    const clicks = await projectService.getActionBarClicks(projectId, normalizedSessionId);

    updateClicks(clicks);
    store.loaded = true;
  } catch {
    // Keep any optimistic entries we already have.
  }
}

export function isActionClicked(entryId: string | undefined, buttonKey: string): boolean {
  return !!entryId && store.byKey.has(keyOf(entryId, buttonKey));
}

function buildOptimisticClick(
  projectId: string, entryId: string, phase: string, buttonKey: string,
): ActionBarClick {
  return {
    id: `optimistic-${keyOf(entryId, buttonKey)}`,
    project_id: projectId, entry_id: entryId, phase, button_key: buttonKey,
    clicked_at: new Date().toISOString(),
  };
}

// eslint-disable-next-line max-statements
export const createActionInvocationId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();

  const bytes = new Uint8Array(16);

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    globalThis.crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0'));

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10).join(''),
  ].join('-');
};

// eslint-disable-next-line max-statements
export async function recordActionClick(
  entryId: string | undefined,
  phase: string,
  buttonKey: string,
  projectIdOverride?: string | null,
  sessionIdOverride?: string | null,
): Promise<void> {
  const projectId = projectIdOverride || store.projectId;
  const sessionId = sessionIdOverride ?? store.sessionId;

  if (!projectId || !entryId || store.byKey.has(keyOf(entryId, buttonKey))) return;
  if (projectIdOverride && (store.projectId !== projectId || store.sessionId !== sessionId)) {
    store.projectId = projectId;
    store.sessionId = sessionId;
    store.loaded = false;
    updateClicks([]);
  }
  const optimistic = buildOptimisticClick(projectId, entryId, phase, buttonKey);

  updateClicks([...store.clicks, optimistic]);

  const real = await projectService.recordActionBarClick(
    projectId,
    entryId,
    phase,
    buttonKey,
    sessionId,
  ).catch(() => null);

  if (!real) return;

  updateClicks(store.clicks.map((c) => (c.id === optimistic.id ? real : c)));
}

/** Record one physical action-button invocation and ask the backend to mirror
 * its exact visible label into chat. Unlike the aggregate selection tracker,
 * every invocation is posted even when this button is already selected. */
export interface RecordUserActionOptions {
  /** Reuse the same request identity when retrying an unacknowledged action. */
  invocationId?: string;
  required?: boolean;
  planningSelection?: PlanningActionSelectionInput;
  baseLabel?: string;
  onRecorded?: (invocationId: string) => void;
}

const invocationFailureStatus = (error: unknown): number | undefined => (
  (error as { status?: number; response?: { status?: number } })?.status
    ?? (error as { response?: { status?: number } })?.response?.status
);

/** A failure with no status (network, timeout, missing run), 408, 429 or 5xx may succeed when sent again. */
const isRetryableInvocationFailure = (error: unknown): boolean => {
  const status = invocationFailureStatus(error);

  return !status || status === 408 || status === 429 || status >= 500;
};

/**
 * Send one invocation up to the admin-configured number of times
 * (`client.action_invocation_attempts`, compiled-in 2), at once and with the
 * same request identity each time. A permanent failure ends the loop.
 *
 * A permanent failure is reported as itself, since it explains the refusal. When
 * the attempts simply run out, the FIRST failure is reported: it describes why
 * the action did not work, where a later attempt often fails differently for a
 * reason the user cannot act on.
 */
async function sendInvocationWithRetries<T>(send: () => Promise<T>, attempt = 1, first?: unknown): Promise<T> {
  try {
    return await send();
  } catch (error) {
    const firstError = attempt === 1 ? error : first;

    if (!isRetryableInvocationFailure(error)) throw error;
    if (attempt >= getClientTimeout('actionInvocationAttempts')) throw firstError;

    return sendInvocationWithRetries(send, attempt + 1, firstError);
  }
}

// eslint-disable-next-line max-statements, max-lines-per-function
export async function recordUserActionClick(
  entryId: string | undefined,
  phase: string,
  buttonKey: string,
  displayedLabel: string,
  projectIdOverride?: string | null,
  sessionIdOverride?: string | null,
  options?: RecordUserActionOptions,
): Promise<string | undefined> {
  const projectId = projectIdOverride || store.projectId;
  const sessionId = sessionIdOverride ?? store.sessionId;

  const normalizedLabel = displayedLabel.trim().slice(0, 160);

  if (!projectId || !normalizedLabel) {
    if (options?.required) throw new Error('The retry action could not be recorded.');

    return undefined;
  }

  const invocationId = options?.invocationId || createActionInvocationId();
  const postInvocation = () => projectService.recordActionBarInvocation(
    projectId, entryId, phase, buttonKey, normalizedLabel, invocationId, sessionId,
    ...(options?.planningSelection ? [{
      planningSelection: options.planningSelection, baseLabel: options.baseLabel || normalizedLabel,
    }] : []),
  ).catch((error: unknown) => {
    if (invocationFailureStatus(error) === 402) signalInsufficientCredits();
    throw error;
  }).then((message) => {
    if (options?.required && !message.planning_run_id
      && !(buttonKey === 'run_artifact_updates' && phase === 'artifact_updates' && message.propagation_id)) {
      throw new Error('The retry task could not be prepared.');
    }

    return message;
  });

  try {
    const message = await sendInvocationWithRetries(postInvocation);

    eventService.emit('chat_message', { type: 'chat_message', ...message });
    if ((message.planning_run_id || message.propagation_id) && eventService.isCurrentProject(projectId)) {
      showPlanningTasks(projectId);
    }
    options?.onRecorded?.(message.id);

    return message.id;
  } catch (error) {
    if (options?.required) throw error;

    return undefined;
  }
}

export const getAllActionBarClicks = (): ActionBarClick[] => [...store.clicks];

/** Merge the click history embedded in an overview entry with the live click
 * store. The metadata makes selection styling refresh-safe, while the store
 * makes it update in the same render cycle as an optimistic click. */
export function selectedActionKeysForEntry(
  entryId: string | undefined,
  metadataButtonKeys: string[] = [],
  clicks: ActionBarClick[] = store.clicks,
): Set<string> {
  const selected = new Set(metadataButtonKeys);

  if (!entryId) return selected;
  clicks.forEach((click) => {
    if (click.entry_id === entryId) selected.add(click.button_key);
  });

  return selected;
}

export function useActionBarClicksStore(): ActionBarClick[] {
  const [, setTick] = useState(0);

  useEffect(() => {
    const listener = () => setTick((t) => t + 1);

    listeners.add(listener);

    return () => { listeners.delete(listener); };
  }, []);

  return store.clicks;
}

export function useActionClick(
  entryId: string | undefined,
  phase: string,
  buttonKey: string,
  projectId?: string | null,
  sessionId?: string | null,
): [boolean, () => void] {
  useActionBarClicksStore();
  const done = isActionClicked(entryId, buttonKey);
  const markDone = useCallback(() => {
    void recordActionClick(entryId, phase, buttonKey, projectId, sessionId);
  }, [entryId, phase, buttonKey, projectId, sessionId]);

  return [done, markDone];
}
